import { QueryClientProvider } from "@tanstack/react-query";
import { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths } from "@/providers/smarta-documents/documents-api-client";
import CustomerAttribute from "@/v1/components/common/CustomerAttribute";
import { api, queryClient } from "../client";

type Props = {
  customerId: string;
  style?: CSSProperties;
};

/**
 * Renders the customer's document intake email (the email-forwarding address
 * `receipts-{token}@smarta.one`) as a copyable customer-attribute cell.
 *
 * The smarta-documents facade is idempotent get-or-mint, so simply querying it
 * generates the address on demand for a customer who doesn't have one yet.
 */
const DocumentIntakeEmailValue = ({ customerId, style }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = api.useQuery(
    "get",
    ApiPaths.getApiCustomersByCustomerIdEmail_intake_address,
    { params: { path: { customerId } } }
  );

  const address = data?.data?.address;

  /*
   * Distinguish the in-flight and failed states from a genuinely empty result:
   * "-" is reserved for a successful response with no address, so operators are
   * not shown a false "no value" while the request is still loading or after it
   * has failed.
   */
  let value: ReactNode;
  if (isLoading) {
    value = "…";
  } else if (isError) {
    value = t("documentIntakeEmailError");
  } else {
    value = address || "-";
  }

  return (
    <CustomerAttribute
      label={t("documentIntakeEmailLabel")}
      dataTest="documentIntakeEmail"
      style={style}
      isCopyable={!!address}
      withFixedIcon
    >
      {value}
    </CustomerAttribute>
  );
};

export const DocumentIntakeEmail = ({ customerId, style }: Props) => (
  <QueryClientProvider client={queryClient}>
    <DocumentIntakeEmailValue customerId={customerId} style={style} />
  </QueryClientProvider>
);
